import React from 'react';
import PropTypes from 'prop-types';
import Module from '../components/module';

import Box from '../components/box';
import Icon from '../components/icon';
import Image from '../components/image';
import Modal from '../components/modal';
import Paragraph from '../components/paragraph';
import PlayButton from '../components/play-button';
import PrimaryButton from '../components/primary-button';
import Title from '../components/title';
import withDevice from '../components/withDevice';
import { CardSliderArrow, CardSliderDots } from '../components/card-slider';

import {
    CarouselProvider,
    Slider,
    Slide,
    WithStore,
} from 'pure-react-carousel';

import _throttle from 'lodash/throttle';

const SlideBackground = withDevice((props) => {
    const {
        image,
        device: { current, isHighDensity },
    } = props;

    let imageUrl = '';
    switch (current) {
        case 'xsm':
        case 'sm':
            imageUrl = image.sm[isHighDensity ? '2x' : '1x'];
            break;
        case 'md':
            imageUrl = image.md[isHighDensity ? '2x' : '1x'];
            break;
        case 'lg':
        case 'xlg':
            imageUrl = image.lg[isHighDensity ? '2x' : '1x'];
            break;
    }

    return (
        <div
            className="video-hero__image"
            style={{ backgroundImage: `url(${imageUrl})` }}
            aria-label={image.alt}></div>
    );
});

const SlideContent = WithStore(
    class extends React.Component {
        static propTypes = {
            eyebrow: PropTypes.string,
            title: PropTypes.string,
            quote: PropTypes.bool,
            desc: PropTypes.string,
            button: PropTypes.shape({
                text: PropTypes.string.isRequired,
                url: PropTypes.string.isRequired,
            }),
            video: PropTypes.shape({
                poster: PropTypes.string,
                mp4: PropTypes.string.isRequired,
                webm: PropTypes.string.isRequired,
                width: PropTypes.number.isRequired,
                height: PropTypes.number.isRequired,
            }),
            index: PropTypes.number.isRequired,
            currentSlide: PropTypes.number.isRequired,
            onPlayClick: PropTypes.func,
        };

        render() {
            const {
                eyebrow,
                title,
                quote,
                desc,
                button,
                video,
                index,
                currentSlide,
                onPlayClick,
            } = this.props;

            return (
                <Box
                    className={`
                    video-hero__content
                    ${index === currentSlide ? '-active' : ''}
                `}>
                    {video && onPlayClick && (
                        <PlayButton
                            className="video-hero__content__play"
                            onClick={onPlayClick}
                        />
                    )}

                    {eyebrow && (
                        <Title
                            level={7}
                            tag="p"
                            className="video-hero__content__eyebrow">
                            {eyebrow}
                        </Title>
                    )}

                    {title && (
                        <Title level={2} className="video-hero__content__title">
                            {quote && (
                                <Icon
                                    className="video-hero__content__quote"
                                    name="quote"
                                />
                            )}
                            {title}
                        </Title>
                    )}

                    {desc && (
                        <Paragraph
                            size="large"
                            className="video-hero__content__desc">
                            {desc}
                        </Paragraph>
                    )}

                    {button && (
                        <PrimaryButton
                            className="video-hero__content__button"
                            href={button.url}
                            title={button.text}
                            tabIndex={0}>
                            {button.text}
                        </PrimaryButton>
                    )}
                </Box>
            );
        }
    },
    (state) => ({
        currentSlide: state.currentSlide,
    }),
);

const SlideNav = WithStore(
    class extends React.Component {
        constructor(props) {
            super(props);
            this._outerRef = React.createRef();
            this._innerRef = React.createRef();
            this._slideRefs = props.slides.map(() => React.createRef());
        }

        componentDidMount() {
            this._updatePos();
        }

        componentDidUpdate(prevProps) {
            if (prevProps.currentSlide !== this.props.currentSlide) {
                this._updatePos();
            }
        }

        onNavItemClick(i) {
            this.props.carouselStore.setStoreState({ currentSlide: i });
        }

        _updatePos() {
            const el = this._slideRefs[this.props.currentSlide].current;
            const outerW = this._outerRef.current.offsetWidth;
            const innerW = this._innerRef.current.scrollWidth;

            let left = el.offsetLeft;

            if (innerW - left < outerW) {
                left = innerW - outerW;
            }

            this._innerRef.current.style.transform = `translateX(-${left}px)`;
        }

        render() {
            const { slides, currentSlide } = this.props;
            return (
                <nav ref={this._outerRef} className="video-hero__nav">
                    <div
                        ref={this._innerRef}
                        className="video-hero__nav__inner">
                        {slides.map((item, i) => (
                            <div
                                key={i}
                                ref={this._slideRefs[i]}
                                role="button"
                                tabIndex={0}
                                className={`video-hero__nav__item ${
                                    currentSlide === i ? '-active' : ''
                                }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    this.onNavItemClick(i);
                                }}
                                onKeyDown={(e) => {
                                    e.keyCode === 32 && e.preventDefault();
                                }}
                                onKeyUp={(e) => {
                                    if (e.keyCode === 32) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        this.onNavItemClick(i);
                                    }
                                }}>
                                <Image
                                    className="video-hero__nav__item__image"
                                    {...item.images.thumb}
                                />
                                {item.eyebrow && (
                                    <Title
                                        level={7}
                                        tag="span"
                                        className="video-hero__nav__item__eyebrow">
                                        {item.eyebrow}
                                    </Title>
                                )}
                                {item.thumbText && (
                                    <Title
                                        level={6}
                                        tag="span"
                                        className="video-hero__nav__item__thumbText">
                                        {item.thumbText}
                                    </Title>
                                )}
                            </div>
                        ))}
                    </div>
                </nav>
            );
        }
    },
    (state) => ({
        currentSlide: state.currentSlide,
        totalSlides: state.totalSlides,
    }),
);

class VideoHero extends Module {
    constructor(props) {
        super(props);
        this.state = {
            modalOpen: false,
            modalVideo: null,
            width: window.innerWidth,
        };
    }

    componentDidMount() {
        window.addEventListener('resize', _throttle(this._handleResize, 100));
    }

    _handleResize = () => {
        this.setState({ width: window.innerWidth });
    };

    _showVideoModal = (video) => {
        this.setState({
            modalOpen: true,
            modalVideo: video,
        });
    };

    render() {
        const {
            config: { slides },
            device,
        } = this.props;
        const { modalOpen, modalVideo, width } = this.state;
        return (
            <section className="video-hero">
                <CarouselProvider
                    naturalSlideWidth={width}
                    naturalSlideHeight={device.isDesktop ? 420 : undefined}
                    isIntrinsicHeight={!device.isDesktop ? true : undefined}
                    totalSlides={slides.length}
                    lockOnWindowScroll={true}
                    visibleSlides={1}
                    infinite={true}>
                    {device.isDesktop && (
                        <>
                            <Slider className="video-hero__images">
                                {slides.map((slide, i) => (
                                    <Slide key={i}>
                                        <SlideBackground
                                            image={slide.images.bg}
                                        />
                                    </Slide>
                                ))}
                            </Slider>

                            <div className="-padded">
                                <div className="video-hero__wrap -wrap">
                                    <div className="video-hero__slide-wrap">
                                        {slides.map((slide, i) => (
                                            <SlideContent
                                                key={i}
                                                index={i}
                                                onPlayClick={(e) => {
                                                    e.preventDefault();
                                                    this._showVideoModal(
                                                        slide.video,
                                                    );
                                                }}
                                                {...slide}
                                            />
                                        ))}
                                    </div>

                                    {slides.length > 1 && (
                                        <>
                                            <div className="video-hero__controls">
                                                <CardSliderArrow
                                                    direction="prev"
                                                    type="button"
                                                    tabIndex={0}
                                                />
                                                <CardSliderArrow
                                                    direction="next"
                                                    type="button"
                                                    tabIndex={0}
                                                />
                                            </div>
                                            <SlideNav slides={slides} />
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {(device.isMobile || device.isTablet) && (
                        <>
                            <Slider className="video-hero__images">
                                {slides.map((slide, i) => (
                                    <Slide key={i}>
                                        <SlideBackground
                                            image={slide.images.bg}
                                        />
                                        <div className="video-hero__slide-wrap">
                                            <SlideContent
                                                index={i}
                                                onPlayClick={(e) => {
                                                    e.preventDefault();
                                                    this._showVideoModal(
                                                        slide.video,
                                                    );
                                                }}
                                                {...slide}
                                            />
                                        </div>
                                    </Slide>
                                ))}
                            </Slider>
                            <CardSliderDots
                                className="video-hero__dots"
                                total={slides.length}
                                variation="alt"
                            />
                        </>
                    )}
                </CarouselProvider>

                {modalOpen && modalVideo && (
                    <Modal
                        open={modalOpen}
                        onClose={() => {
                            this.setState({
                                modalOpen: false,
                                modalVideo: null,
                            });
                        }}>
                        <div
                            className="video-hero__video"
                            style={{
                                width: `${modalVideo.width}px`,
                                paddingTop: `${
                                    (modalVideo.height / modalVideo.width) * 100
                                }%`,
                            }}>
                            <video
                                className="video-hero__video__el"
                                width={modalVideo.width}
                                height={modalVideo.height}
                                poster={modalVideo.poster}
                                controls
                                autoPlay>
                                <source src={modalVideo.mp4} type="video/mp4" />
                                <source
                                    src={modalVideo.webm}
                                    type="video/webm"
                                />
                            </video>
                        </div>
                    </Modal>
                )}
            </section>
        );
    }
}

export default withDevice(VideoHero);
